import { getBreakdownTitle } from 'checkout/utils'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { sum } from 'lib/arrayUtils'
import { formatOccupantsShort, sumUpOccupancies } from 'lib/occupancyUtils'
import { pluralizeToString } from 'lib/pluralize'
import formatRoomType from 'lib/TourV2/formatRoomType'
import generateDepartureRoomPriceBreakdown from 'lib/TourV2/generateDepartureRoomPriceBreakdown'
import moment from 'moment'
import createSelector from 'lib/createSelector'
import { isTourV2Item, isTourV2ItemWithPackageUpgrade } from 'lib/checkout'
import { arrayResultMemoizeOptions } from 'checkout/lib/utils/misc/arrayResultMemoizeOptions'
import formatPackageOptionType from 'lib/TourV2/formatPackageOptionType'

export const getTourV2ItemsForPackageUpgrade = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.TourV2Item> => items.filter(isTourV2ItemWithPackageUpgrade),
  arrayResultMemoizeOptions,
)

export const getTourV2Items = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.TourV2Item> => items.filter(isTourV2Item),
  arrayResultMemoizeOptions,
)

export function getTourV2BreakdownView(
  offerView: App.Checkout.TourV2AccommodationOfferView,
  hidePrice: boolean,
): App.Checkout.PriceBreakdownView {
  const { durationLabel, startDate, endDate, saleUnit, itemViews, offer, mainLabel } = offerView

  const guestCount = sumUpOccupancies(itemViews.map(itemView => itemView.occupancy))

  const items = itemViews.map<App.Checkout.ItemBreakdownView>((item: App.Checkout.TourV2AccommodationItemView, index) => {
    const purchasableOption = item.item.purchasableOption
    const { totalPrice } = generateDepartureRoomPriceBreakdown(
      item.occupancy,
      purchasableOption,
      purchasableOption.fkRoomTypePricingId ? offer?.roomTypePricing[purchasableOption.fkRoomTypePricingId] : undefined,
      offer?.variations[purchasableOption.fkVariationId],
    )
    const adultLabel = offer?.isLeTour ? 'traveller' : 'adult'
    return {
      title: `${saleUnit} ${index + 1}`,
      additionalInfoText: [
          `${formatOccupantsShort(item.occupancy, undefined, adultLabel)}${purchasableOption.roomType ? ' • ' + formatRoomType(purchasableOption.roomType) : ''}`,
      ],
      additionalElements: [],
      price: hidePrice ? undefined : totalPrice,
      offerId: offer?.id,
      itemType: 'tourV2',
      taxesAndFees: item.taxesAndFees,
      packageOption: formatPackageOptionType(purchasableOption?.roomType ?? ''),
    }
  })
  const purchasableOption = itemViews?.[0]?.item?.purchasableOption
  const selectedVariation = purchasableOption?.fkVariationId ? offer?.variations[purchasableOption?.fkVariationId] : undefined

  return {
    title: getBreakdownTitle(offerView),
    offerType: offer?.type,
    description: mainLabel,
    additionalInfoText: [
        `${moment(startDate).format(DMY_CASUAL_FORMAT)} - ${moment(endDate).format(DMY_CASUAL_FORMAT)}`,
        `${durationLabel} (${pluralizeToString('room', itemViews.length)} • ${pluralizeToString('guest', guestCount)})`,
    ],
    items,
    itemsAlwaysVisible: true,
    price: sum(items, item => item.price || 0),
    variation: selectedVariation,
  }
}
